import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { RawIntlProvider } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getCurrentIntl } from '../../i18n/intl/intl';
import { Language } from '../../interfaces/Language';
import { fetchTenantData } from '../../slices/tenantSlices';
import { AppDispatch } from '../../utils/store';

import { WB_COMPANY_SLUG } from '~/assets/utils/axiosInstance';
import { useAuthSlice } from '~/slices/auth';

interface LanguageProviderProps {
  children: ReactNode;
}

export interface LanguageState {
  selectedLanguage?: Language;
  setLanguage: (value: Language) => void;
}

// Manually set the default language
// Values are based on the language data in the database
export const DEFAULT_LANGUAGE = { name: 'English', code: 'en', id: 19 };

const getCurrentLanguage = (): Language => {
  const storedLanguage = localStorage.getItem('lang');
  if (!storedLanguage) {
    return DEFAULT_LANGUAGE;
  }

  return JSON.parse(storedLanguage);
};

export const isCurrentLanguageDefault = (() => getCurrentLanguage().code === DEFAULT_LANGUAGE.code);

export const LanguageContext = createContext<LanguageState | null>(null);

const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const LOCAL_STORAGE_LANG_KEY = 'lang';
  const [selectedLanguage, setSelectedLanguage] = useState(getCurrentLanguage());
  const intl = getCurrentIntl();
  const isFetchingRef = useRef(false);
  const region = useAuthSlice().auth.user.region;

  const dispatch = useDispatch<AppDispatch>();

  // Extract the language choices in tenant data using program-overview api
  useEffect(() => {
    /*
    Prevent fetching public tenant data if the slug is not available,
    this effect is not applicable for customer pages with "app" as the only subdomain
    */
    if (isFetchingRef.current || !WB_COMPANY_SLUG || !region) return;
    isFetchingRef.current = true;

    dispatch(
      fetchTenantData({ type: 'public', language_id: selectedLanguage.id.toString() }),
    )
      .finally(() => {
        isFetchingRef.current = false;
      })
      .catch(() => {
        isFetchingRef.current = false;
      });
  }, [dispatch, selectedLanguage.id, isFetchingRef, region]);

  const setLanguage = useCallback(
    (data: Language) => {
      const stringifiedLanguage = JSON.stringify(data);

      localStorage.setItem(LOCAL_STORAGE_LANG_KEY, stringifiedLanguage);
      setSelectedLanguage(data);
    },
    [LOCAL_STORAGE_LANG_KEY],
  );

  return (
    <LanguageContext.Provider
      value={{
        selectedLanguage,
        setLanguage,
      }}
    >
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return {
    selectedLanguage: context.selectedLanguage ?? DEFAULT_LANGUAGE,
    setLanguage: context.setLanguage,
  };
};
